import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';
import { BrandWordmark } from '../components/BrandWordmark.tsx';
import { Modal } from '../components/Modal.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { LocaleSwitcher, useT } from '../i18n/index.ts';
import { Button, Chip, Icon, cx } from '../ui/index.ts';
import { ShellBell } from './ShellBell.tsx';
import { ShellCommandPalette } from './ShellCommandPalette.tsx';
import { ShellNavList } from './ShellNavList.tsx';
import { activeNavItemForPath, visibleNavItems } from './navigation.ts';
import { pushRecentPath } from './recentPaths.ts';

/**
 * Nxtpath tenant app chrome — sidebar + header + main area. Renders the
 * authenticated experience for every tenant-facing page under the root
 * route tree (see `App.tsx`).
 *
 * Visual contract: DESIGN.md (Field Notes, epic #950). The sidebar is
 * the ledger's contents page: same `canvas` paper as the main area,
 * separated by a hairline — layers by lines, not fills (§1.1). The
 * 56px header is a thin toolbar; page titles belong to each page's
 * serif `PageHeader`, the header only echoes the section name.
 *
 * Layout behaviour:
 *   - The sidebar is a persistent column from `lg` up and collapses to
 *     a 56px glyph rail (persisted in `console.sidebar.rail`). Below
 *     `lg` it is not rendered as a column at all — it moves behind the
 *     header hamburger as a drawer. The previous fixed 256px column had
 *     no breakpoint handling and left 119px of content at 375px.
 *   - Below `sm` the header title goes `sr-only`. It is the only
 *     flexible item in a row whose other two are `shrink-0`, so at 375px
 *     it was left 53px against a 119px `Balance & billing` and rendered
 *     the stub `Balan…`. A stub is worth less than nothing — it reads as
 *     a broken render — and nothing is lost by dropping it, because
 *     every routed page opens with its own full-size display heading.
 *     `sr-only` rather than `hidden` because on `/keys`, `/usage`,
 *     `/organization` and `/optimization` this is the document's only
 *     `<h1>` (those pages head their own content with `<h2>`), and
 *     `display: none` would leave them with no `<h1>` at all on a phone.
 *   - ⌘K (Ctrl+K off mac) opens the command palette; every visited nav
 *     destination is recorded for its "recent" group.
 */
interface Props {
  readonly children?: ReactNode;
}

const RAIL_STORAGE_KEY = 'console.sidebar.rail';

function loadRailPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function Shell({ children }: Props): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();

  const [rail, setRail] = useState(loadRailPreference);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const navItems = useMemo(() => visibleNavItems(user), [user]);
  const activeNavItem = useMemo(
    () => activeNavItemForPath(user, location.pathname),
    [location.pathname, user],
  );
  const activePath = activeNavItem?.to ?? null;
  const pageTitle =
    activeNavItem !== null ? t(activeNavItem.labelKey) : t('shell.page.console_fallback');

  // Record nav destinations for the palette's "recent" group, and close
  // the drawer after any navigation — a drawer left open over the page
  // the tenant just asked for is the classic mobile-nav bug.
  useEffect(() => {
    if (activePath !== null) {
      pushRecentPath(activePath);
    }
    setMobileNavOpen(false);
  }, [activePath, location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  function toggleRail(): void {
    setRail((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(RAIL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Preference only — a failure here must not break the toggle.
      }
      return next;
    });
  }

  async function handleLogout(): Promise<void> {
    try {
      await platformApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own TTL regardless.
      if (!(err instanceof PlatformApiError)) {
        throw err;
      }
    }
    clearUser();
    navigate('/login', { replace: true });
  }

  const displayEmail =
    user?.email !== undefined && user.email.length > 0 ? user.email : t('shell.session.signed_in');
  const isVerified = user?.emailVerified === true;
  const verifiedBadge = isVerified ? t('shell.session.verified') : t('shell.session.unverified');

  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-ink">
      <aside
        data-testid="shell-sidebar"
        className={cx(
          'hidden min-h-0 shrink-0 flex-col border-r border-line bg-canvas py-6 transition-[width] duration-150 lg:flex',
          rail ? 'w-14 px-1.5' : 'w-64 px-4',
        )}
      >
        <div className={cx('mb-8 shrink-0', !rail && 'px-2')}>
          {rail ? (
            <span className="sr-only" data-testid="shell-brand-wordmark">
              {t('common.brand.title')}
            </span>
          ) : (
            <>
              <BrandWordmark descriptor={t('shell.brand.subtitle')} testId="shell-brand-wordmark" />
              <p className="mt-4 border-l border-line pl-3 text-caption text-ink-low">
                {t('shell.brand.supporting')}
              </p>
            </>
          )}
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain"
        >
          <ShellNavList
            items={navItems}
            activePath={activePath}
            testIdPrefix="shell-nav"
            rail={rail}
          />
        </nav>
        <div className="mt-auto flex shrink-0 flex-col gap-3 pt-6">
          {rail ? (
            <span className="sr-only" data-testid="shell-email">
              {displayEmail}
            </span>
          ) : (
            <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-card px-3 py-3">
              <span
                className="truncate text-caption font-medium text-ink"
                data-testid="shell-email"
              >
                {displayEmail}
              </span>
              <Chip
                tone={isVerified ? 'ok' : 'warn'}
                lamp={false}
                className="w-fit"
                testId="shell-verified"
              >
                {verifiedBadge}
              </Chip>
            </div>
          )}
          <Button
            variant="secondary"
            aria-label={t('shell.logout')}
            onClick={() => {
              void handleLogout();
            }}
            data-testid="shell-logout"
            className="w-full"
          >
            <Icon name="logout" size="sm" />
            <span className={cx(rail && 'sr-only')}>{t('shell.logout')}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
            onClick={toggleRail}
            data-testid="shell-rail-toggle"
            className={cx('w-full', !rail && 'justify-start')}
          >
            <Icon name={rail ? 'left_panel_open' : 'left_panel_close'} size="sm" />
            <span className={cx(rail && 'sr-only')}>
              {rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
            </span>
          </Button>
        </div>
      </aside>

      <Modal
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        titleId="shell-mobile-nav-title"
        testId="shell-mobile-nav"
        variant="drawer-start"
      >
        <div className="flex items-start justify-between gap-2 border-b border-line px-4 py-4">
          <h2 id="shell-mobile-nav-title" className="text-heading text-ink">
            {t('common.brand.title')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('shell.nav.close')}
            data-testid="shell-mobile-nav-close"
            onClick={() => setMobileNavOpen(false)}
          >
            <Icon name="close" size="sm" />
          </Button>
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain p-2"
        >
          <ShellNavList
            items={navItems}
            activePath={activePath}
            testIdPrefix="shell-mobile-nav"
            onNavigate={() => setMobileNavOpen(false)}
          />
        </nav>
        <div className="mt-auto flex shrink-0 items-center justify-between gap-2 border-t border-line px-4 py-3">
          <span className="min-w-0 truncate text-caption text-ink-low">{displayEmail}</span>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('shell.logout')}
            data-testid="shell-mobile-nav-logout"
            onClick={() => {
              void handleLogout();
            }}
          >
            <Icon name="logout" size="sm" />
          </Button>
        </div>
      </Modal>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-canvas px-8 max-lg:px-4">
          <Button
            variant="ghost"
            aria-label={t('shell.nav.open')}
            onClick={() => setMobileNavOpen(true)}
            data-testid="shell-mobile-nav-open"
            className="shrink-0 px-2 lg:hidden"
          >
            <Icon name="menu" size="sm" />
          </Button>
          <h1
            className="min-w-0 truncate text-heading text-ink max-sm:sr-only"
            data-testid="shell-page-title"
          >
            {pageTitle}
          </h1>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="shell-cmdk-trigger"
            className="ml-2 hidden h-8 min-w-0 max-w-md flex-1 items-center gap-2 rounded border border-line bg-well px-3 text-caption text-ink-low transition-colors duration-150 hover:border-line-strong hover:text-ink-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 md:flex"
          >
            <Icon name="search" size="xs" />
            <span className="truncate">{t('shell.cmdk.trigger')}</span>
            <kbd className="ml-auto shrink-0 rounded border border-line px-1.5 font-mono text-caption text-ink-low">
              ⌘K
            </kbd>
          </button>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <Button
              variant="ghost"
              aria-label={t('shell.cmdk.trigger')}
              onClick={() => setPaletteOpen(true)}
              data-testid="shell-cmdk-trigger-compact"
              className="px-2 md:hidden"
            >
              <Icon name="search" size="sm" />
            </Button>
            <ShellBell />
            <LocaleSwitcher />
            <Chip tone="neutral" lamp className="max-lg:hidden">
              {t('shell.header.tenant_chip')}
            </Chip>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-8 max-lg:p-4">
          {children ?? <Outlet />}
        </main>
      </div>

      <ShellCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
